// The "Venues near you" map: what the public list holds, and the sums the page does on it.

export interface PublicVenue {
  id: number;
  name: string;
  lat: number;
  lng: number;
  street: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  logoUrl: string | null;
}

export interface LatLng {
  lat: number;
  lng: number;
}

/** The owner's view of their venue's map entry, from GET /venues/player/:id. */
export interface VenueEntry {
  enabled: boolean;
  displayName: string;
  status: 'pending' | 'confirmed' | null;
  lat: number | null;
  lng: number | null;
  street: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  confirmedAt: string | null;
  /** After saving an address: whether it was found on the map. */
  outcome?: 'found' | 'not_found' | 'lookup_failed' | 'unchanged';
}

export type AddressFields = Pick<VenueEntry, 'street' | 'city' | 'region' | 'postalCode' | 'country'>;

/** One line, the way an envelope would have it. */
export const formatAddress = (a: AddressFields): string =>
  [a.street, a.city, [a.region, a.postalCode].filter(Boolean).join(' '), a.country].filter(Boolean).join(', ');

const EARTH_KM = 6371;
const radians = (degrees: number) => (degrees * Math.PI) / 180;

/** Straight-line distance in kilometres. */
export const distanceKm = (from: LatLng, to: LatLng): number => {
  const dLat = radians(to.lat - from.lat);
  const dLng = radians(to.lng - from.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(radians(from.lat)) * Math.cos(radians(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)));
};

/** Miles where the visitor's browser speaks US or UK English, kilometres elsewhere. */
export const usesMiles = (language: string = typeof navigator !== 'undefined' ? navigator.language : 'en-US') =>
  /^en-(US|GB|LR|MM)$/i.test(language || '');

export const formatDistance = (km: number, miles = usesMiles()): string => {
  const value = miles ? km * 0.621371 : km;
  const unit = miles ? 'mi' : 'km';
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${unit}`;
};

export interface VenueWithDistance extends PublicVenue {
  km: number | null;
}

/** Nearest first when the visitor's location is known, otherwise by name. */
export const sortVenues = (venues: PublicVenue[], from: LatLng | null): VenueWithDistance[] => {
  const withDistance = venues.map(venue => ({ ...venue, km: from ? distanceKm(from, venue) : null }));
  return withDistance.sort((a, b) =>
    a.km != null && b.km != null && a.km !== b.km ? a.km - b.km : a.name.localeCompare(b.name),
  );
};

/** Directions by address, falling back to the pin when there is no address. */
export const directionsUrls = (venue: PublicVenue) => {
  const address = formatAddress(venue);
  const destination = address ? `${venue.name}, ${address}` : `${venue.lat},${venue.lng}`;
  const q = encodeURIComponent(destination);
  return {
    google: `https://www.google.com/maps/dir/?api=1&destination=${q}`,
    apple: `https://maps.apple.com/?daddr=${q}`,
  };
};

/** Apple devices get Apple Maps; everyone else Google Maps. */
export const prefersAppleMaps = (userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent : '') =>
  /iPhone|iPad|iPod|Macintosh/i.test(userAgent || '');
